import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, resource, signal, DestroyRef, computed, effect } from "@angular/core";
import { firstValueFrom, Subject, filter, take, combineLatest } from "rxjs";
import type { ITaxReturnStatus } from "@app/views-interfaces";
import { ApplicationType, isJobsBilling, isiFirmTax, isTaxprep } from "@app/views-interfaces";
import { AppSettingsService } from "@shared/app-settings-service";
import { DashboardsService } from "@shared/dashboards-service";
import { IfirmTaxReturnStatusesService } from "@shared/ifirm-tax-return-status-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import type {
    ITaxReturnSingleStatusResponse,
    ITaxReturnStatusDeleteResponse,
    ITaxReturnStatusListResponse,
} from "./tax-return-status-model-httpResponseRequest.interface";
import { JobsAndBillingStatusesService } from "@shared/jobs-and-billing-statuses-service";
import { AuthService } from "@shared/auth";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { BroadcastService } from "@shared/broadcast-service";
import { IframeMessage } from "@shared/iframe-messages-service";

interface TaxReturnStatusesResource {
    applicationType: ApplicationType | undefined;
    dashboardId: string;
}

type DashboardTypeTaxReturnStatusesMap = Map<ApplicationType | string, ITaxReturnStatus[]>;

@Injectable({ providedIn: "root" })
export class TaxReturnStatusesService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly ifirmTaxReturnStatusesService = inject(IfirmTaxReturnStatusesService);
    private readonly jobsAndBillingStatusesService = inject(JobsAndBillingStatusesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly http = inject(HttpClient);

    private isReloading = true;
    private isLoading = true;
    private refreshTimer = setInterval(() => this.reload(), 3600000);
    private taxReturnStatusesLoadedEmitter = new Subject();
    public taxReturnStatusesLoadedEvent$ = this.taxReturnStatusesLoadedEmitter.asObservable();
    private taxReturnStatuses = signal<DashboardTypeTaxReturnStatusesMap>(new Map());

    private taxReturnStatusesResource = resource<ITaxReturnStatus[] | undefined, TaxReturnStatusesResource>({
        params: () => ({
            applicationType: this.dashboardsService.currentApplicationType(),
            dashboardId: this.dashboardsService.currentId(),
        }),
        loader: async ({ params }) => {
            try {
                const { applicationType, dashboardId } = params;
                if (!applicationType || !dashboardId) return;

                const key = isTaxprep(applicationType) ? dashboardId : applicationType;
                if (!this.isReloading && this.taxReturnStatuses().has(key)) return this.taxReturnStatuses().get(key);

                this.isReloading = false;
                const statuses = await this.loadTaxReturnStatuses(applicationType);
                if (statuses) {
                    this.taxReturnStatuses.update((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(key, statuses);
                        return newMap;
                    });
                }

                return statuses;
            } finally {
                this.isLoading = false;
            }
        },
    });
    private taxReturnStatusesResourceStatus$ = toObservable(this.taxReturnStatusesResource.status);
    public currentTaxReturnStatuses = computed(() => this.taxReturnStatusesResource.value());
    private currentTaxReturnStatuses$ = toObservable(this.currentTaxReturnStatuses);

    constructor() {
        this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            this.isLoading = true;
        });

        this.destroyRef.onDestroy(() => clearInterval(this.refreshTimer));

        BroadcastService.on<IframeMessage>("sessionChanged")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async () => this.reload());

        effect(() => {
            const statuses = this.taxReturnStatusesResource.value();
            if (!statuses) return;

            this.taxReturnStatusesLoadedEmitter.next(true);
        });
    }

    public reload(): void {
        if (this.authService.inReloginPrompted()) return;
        this.isReloading = true;
        this.taxReturnStatusesResource.reload();
    }

    private async loadTaxReturnStatuses(applicationType: ApplicationType): Promise<ITaxReturnStatus[] | undefined> {
        try {
            if (isiFirmTax(applicationType)) {
                return await this.ifirmTaxReturnStatusesService.getReturnStatuses(applicationType);
            } else if (isJobsBilling(applicationType)) {
                return await this.jobsAndBillingStatusesService.getJobStatuses();
            } else {
                const request = this.http.get<ITaxReturnStatusListResponse>(this.getUrl(), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                });
                const result = await firstValueFrom(request);
                if (result.Code === 200) {
                    const taxReturnStatuses = result.TaxReturnStatuses?.map((status) => ({
                        ...status,
                        Active: true,
                    }));
                    return taxReturnStatuses;
                }
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    public addStatus(statusData: ITaxReturnStatus) {
        return this.http.post<ITaxReturnSingleStatusResponse>(this.getUrl(), statusData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    public updateStatus(statusId: string, statusData: ITaxReturnStatus) {
        return this.http.put<ITaxReturnSingleStatusResponse>(this.getUrl() + statusId, statusData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    public deleteStatus(statusId: string) {
        return this.http.delete<ITaxReturnStatusDeleteResponse>(this.getUrl() + statusId);
    }

    public async getTaxReturnStatusesList(): Promise<ITaxReturnStatus[]> {
        await this.waitForTaxReturnStatuses();
        return this.currentTaxReturnStatuses() ?? [];
    }

    public async getTaxReturnStatusById(id: string | undefined): Promise<ITaxReturnStatus | undefined> {
        if (!id) return;
        const taxReturnStatusesList = await this.getTaxReturnStatusesList();
        const taxReturnStatus = taxReturnStatusesList.find((it) => it.Id === id);
        return taxReturnStatus;
    }

    // Do not use it. Only use in getTaxReturnStatusesList() for fallback compatibility. It will be removed in the future.
    private async waitForTaxReturnStatuses(): Promise<void> {
        await firstValueFrom(
            combineLatest([this.taxReturnStatusesResourceStatus$, this.currentTaxReturnStatuses$]).pipe(
                filter(
                    ([status, currentTaxReturnStatuses]) =>
                        !this.isLoading &&
                        status !== "loading" &&
                        status !== "reloading" &&
                        currentTaxReturnStatuses !== undefined &&
                        currentTaxReturnStatuses !== null,
                ),
                take(1),
            ),
        );
    }

    private getUrl(path = "tax-return-status/") {
        return (
            (this.appSettingsService.getSetting("TaxReturnStatusesService_URL") ?? "") +
            (this.appSettingsService.getSetting("TaxReturnStatusesService_Version") ?? "") +
            path +
            this.dashboardsService.currentId() +
            "/"
        );
    }
}
